import { z } from 'zod';
import { defineEndpoint } from '../contract.ts';
import { timestamp } from '../schemas.ts';

// Group administration (design §6.2, §6.11). Every change needs a recent login.

const RecordId = z.string().regex(/^[a-z0-9]{15}$/);
const DiscordId = z.string().regex(/^[0-9]{1,32}$/);

export const GroupMember = z.object({
  userId: z.string(),
  username: z.string(),
  displayName: z.string(),
  /** The admin who added them, if known. */
  addedBy: z.string().nullable(),
  expiresAt: timestamp.nullable(),
  /** False once the membership has expired. */
  active: z.boolean(),
  created: timestamp,
});
export type GroupMember = z.infer<typeof GroupMember>;

export const GroupSummary = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  /** Empty for Administrators, which holds every permission. */
  permissions: z.array(z.string()),
  discordRoleIds: z.array(z.string()),
  /** The built-in Administrators group. */
  builtin: z.boolean(),
  /** Manual members, active or not. Role holders aren't counted. */
  memberCount: z.number().int(),
  version: z.number().int(),
  created: timestamp,
  updated: timestamp,
});
export type GroupSummary = z.infer<typeof GroupSummary>;

/** An active user who reaches the group by holding one of its linked Discord roles. */
export const RoleMember = z.object({ userId: z.string(), username: z.string(), displayName: z.string(), roleIds: z.array(z.string()) });
export type RoleMember = z.infer<typeof RoleMember>;

export const GroupDetail = GroupSummary.extend({
  members: z.array(GroupMember),
  /** Shown apart from manual members: these follow core_users.roles, kept current by the worker. */
  roleMembers: z.array(RoleMember),
});
export type GroupDetail = z.infer<typeof GroupDetail>;

const GroupParams = z.object({ id: RecordId });

const GroupFields = {
  name: z.string().trim().min(1).max(64),
  description: z.string().max(500),
  permissions: z.array(z.string().max(128)).max(500),
  discordRoleIds: z.array(DiscordId).max(100),
};

export const listGroups = defineEndpoint({
  method: 'GET',
  path: '/admin/groups',
  permission: 'core.admin.groups',
  response: z.object({ items: z.array(GroupSummary) }),
});

export const getGroup = defineEndpoint({
  method: 'GET',
  path: '/admin/groups/:id',
  permission: 'core.admin.groups',
  params: GroupParams,
  response: GroupDetail,
});

export const createGroup = defineEndpoint({
  method: 'POST',
  path: '/admin/groups',
  permission: 'core.admin.groups',
  recentLogin: true,
  body: z.object({
    name: GroupFields.name,
    description: GroupFields.description.default(''),
    permissions: GroupFields.permissions.default([]),
    discordRoleIds: GroupFields.discordRoleIds.default([]),
  }),
  response: GroupDetail,
});

export const updateGroup = defineEndpoint({
  method: 'PATCH',
  path: '/admin/groups/:id',
  permission: 'core.admin.groups',
  recentLogin: true,
  params: GroupParams,
  body: z.object({
    /** The version the editor loaded; a stale one is 409 conflict. */
    version: z.number().int(),
    name: GroupFields.name.optional(),
    description: GroupFields.description.optional(),
    permissions: GroupFields.permissions.optional(),
    discordRoleIds: GroupFields.discordRoleIds.optional(),
  }),
  response: GroupDetail,
});

export const deleteGroup = defineEndpoint({
  method: 'DELETE',
  path: '/admin/groups/:id',
  permission: 'core.admin.groups',
  recentLogin: true,
  params: GroupParams,
  query: z.object({ version: z.coerce.number().int() }),
  response: z.object({ ok: z.literal(true) }),
});

export const addGroupMember = defineEndpoint({
  method: 'POST',
  path: '/admin/groups/:id/members',
  permission: 'core.admin.groups',
  recentLogin: true,
  params: GroupParams,
  /** Adding an existing member changes their expiry. */
  body: z.object({ userId: RecordId, expiresAt: z.iso.datetime().nullable().default(null) }),
  response: GroupDetail,
});

export const removeGroupMember = defineEndpoint({
  method: 'DELETE',
  path: '/admin/groups/:id/members/:userId',
  permission: 'core.admin.groups',
  recentLogin: true,
  params: z.object({ id: RecordId, userId: RecordId }),
  response: GroupDetail,
});
